// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The snap guides on the bench, in Three (M13-13).
//
// `snapView.ts` says what a snap looks like; this draws it, in the playfield's own frame under
// `MachineScene.playfield` so a line to a partner's anchor lands on that anchor. Four lines, two
// marks, one reticle and one label, made once and moved every frame — a carry is seventy-two
// frames a second and a guide that allocated per frame would be the picture's own garbage
// collector. The lines are drawn through everything, as the halo is: a guide hidden behind the
// part it explains explains nothing.
//
// **The flash is the light half of the sound-and-light twin.** On the frame a rule changes the
// marks jump to `FLASH_SCALE` and settle over `FLASH_SECONDS`; the runtime clicks or ticks on the
// same frame. Both clients do exactly this, in the `guide` finish (M13-18's table), so a rule
// caught on the browser looks like one caught natively.
//
// The label is a sprite over the point, drawn on a canvas as the wrist panel is, and only
// redrawn when the words change. A build without a canvas — a test, a browser that refused a
// 2-D context — draws every line and mark and simply has no words.

import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Group,
  Line,
  LineBasicMaterial,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  RingGeometry,
  SRGBColorSpace,
  Sprite,
  SpriteMaterial,
} from 'three'
import type { SnapView } from '../xr/build/snapView'
import { FINISHES } from './partsKit'

/** The guides' colour: the `guide` finish, the 2-D canvas's snap pink, on both clients. */
export const GUIDE_COLOR = FINISHES.guide.color
export const GUIDE_OPACITY = FINISHES.guide.opacity ?? 0.9
/** The reticle at the raw point: the ghost's own pale blue, so it reads as "the hand", not "a rule". */
export const RETICLE_COLOR = FINISHES.ghost.color

/** A mark's ring, playfield metres: smaller than the halo, larger than a pinch dot. */
export const MARK_INNER = 0.009
export const MARK_OUTER = 0.012
/** The reticle's ring: a fingertip. */
export const RETICLE_INNER = 0.004
export const RETICLE_OUTER = 0.006
/** How much a mark grows on the frame a rule changes, and how long it takes to settle. */
export const FLASH_SCALE = 1.8
export const FLASH_SECONDS = 0.18
/** Lift off the playfield surface, metres, so the guides are not in the paint. */
const LIFT = 0.002
/** The label, playfield metres, and where it sits over the point. */
export const LABEL_WIDTH = 0.16
export const LABEL_HEIGHT = 0.02
/**
 * The label hangs *nearer the author* than the point, not beyond it: a part in hand is a body a
 * few centimetres tall standing on the point, and words behind it are words inside it.
 */
const LABEL_BELOW = 0.06
/** Eight to one, as the native `TrayTileArt.Caption` is. */
const LABEL_CANVAS = { width: 640, height: 80 }
const MAX_LINES = 4
const MAX_MARKS = 2

export interface SnapGuidesBuild {
  readonly group: Group
  /** This frame's view, or nothing; `flash` is true on the frame a rule changed. */
  update(view: SnapView | undefined, seconds: number, flash: boolean): void
  /** The words on the label, for a test with no canvas to read. */
  readonly label: string | undefined
  dispose(): void
}

export function buildSnapGuides(): SnapGuidesBuild {
  const group = new Group()
  group.name = 'build.guides'
  group.visible = false

  const lineMaterial = new LineBasicMaterial({
    color: GUIDE_COLOR,
    transparent: true,
    opacity: GUIDE_OPACITY,
    depthTest: false,
    depthWrite: false,
  })
  const lines = Array.from({ length: MAX_LINES }, (_, i) => {
    const geometry = new BufferGeometry()
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(6), 3))
    const line = new Line(geometry, lineMaterial)
    line.name = `guide-line-${i}`
    line.renderOrder = 1000
    line.visible = false
    line.frustumCulled = false
    group.add(line)
    return { line, geometry }
  })

  const markGeometry = new RingGeometry(MARK_INNER, MARK_OUTER, 32)
  const markMaterial = new MeshBasicMaterial({
    color: GUIDE_COLOR,
    transparent: true,
    opacity: GUIDE_OPACITY,
    depthTest: false,
    depthWrite: false,
  })
  const marks = Array.from({ length: MAX_MARKS }, (_, i) => {
    const mesh = new Mesh(markGeometry, markMaterial)
    mesh.name = `guide-mark-${i}`
    mesh.renderOrder = 1000
    mesh.visible = false
    group.add(mesh)
    return mesh
  })

  const reticleGeometry = new RingGeometry(RETICLE_INNER, RETICLE_OUTER, 24)
  const reticleMaterial = new MeshBasicMaterial({
    color: RETICLE_COLOR,
    transparent: true,
    opacity: 0.9,
    depthTest: false,
    depthWrite: false,
  })
  const reticle = new Mesh(reticleGeometry, reticleMaterial)
  reticle.name = 'guide-reticle'
  reticle.renderOrder = 1000
  reticle.visible = false
  group.add(reticle)

  // The label: a canvas where there is one.
  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } }).document
  const canvas = host?.createElement('canvas')
  const ctx = canvas?.getContext('2d') ?? null
  if (canvas !== undefined) {
    canvas.width = LABEL_CANVAS.width
    canvas.height = LABEL_CANVAS.height
  }
  const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
  if (texture !== undefined) {
    texture.colorSpace = SRGBColorSpace
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
  }
  const labelMaterial = new SpriteMaterial({
    ...(texture === undefined ? { color: GUIDE_COLOR } : { map: texture }),
    transparent: true,
    depthTest: false,
    depthWrite: false,
  })
  const label = new Sprite(labelMaterial)
  label.name = 'guide-label'
  label.scale.set(LABEL_WIDTH, LABEL_HEIGHT, 1)
  label.renderOrder = 1001
  label.visible = false
  group.add(label)

  let words: string | undefined
  let flashLeft = 0

  const paint = (text: string): void => {
    if (ctx === null || canvas === undefined || texture === undefined) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = 'rgba(12, 10, 20, 0.78)'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    ctx.fillStyle = '#ff7ab6'
    ctx.font = '600 34px system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(text, canvas.width / 2, canvas.height / 2, canvas.width - 16)
    texture.needsUpdate = true
  }

  const setLine = (i: number, from: readonly number[], to: readonly number[]): void => {
    const entry = lines[i]
    if (entry === undefined) return
    const position = entry.geometry.getAttribute('position') as BufferAttribute
    position.setXYZ(0, from[0] ?? 0, from[1] ?? 0, LIFT)
    position.setXYZ(1, to[0] ?? 0, to[1] ?? 0, LIFT)
    position.needsUpdate = true
    entry.line.visible = true
  }

  return {
    group,
    get label() {
      return words
    },
    update(view, seconds, flash) {
      if (view === undefined) {
        group.visible = false
        words = undefined
        flashLeft = 0
        return
      }
      group.visible = true
      for (let i = 0; i < MAX_LINES; i++) {
        const entry = lines[i]
        if (entry !== undefined) entry.line.visible = false
      }
      view.lines.slice(0, MAX_LINES).forEach((line, i) => setLine(i, line.from, line.to))

      if (flash) flashLeft = FLASH_SECONDS
      else flashLeft = Math.max(0, flashLeft - Math.max(0, seconds))
      const grown = 1 + (FLASH_SCALE - 1) * (FLASH_SECONDS > 0 ? flashLeft / FLASH_SECONDS : 0)
      marks.forEach((mesh, i) => {
        const mark = view.marks[i]
        mesh.visible = mark !== undefined
        if (mark === undefined) return
        mesh.position.set(mark.at[0], mark.at[1], LIFT)
        mesh.scale.set(grown, grown, 1)
      })

      const raw = view.raw
      reticle.visible = raw !== undefined
      if (raw !== undefined) reticle.position.set(raw[0], raw[1], LIFT)

      const text = view.label
      label.visible = text !== undefined
      if (text !== undefined) {
        label.position.set(view.at[0], view.at[1] - LABEL_BELOW, LIFT + 0.02)
        if (text !== words) paint(text)
      }
      words = text
    },
    dispose() {
      for (const entry of lines) entry.geometry.dispose()
      lineMaterial.dispose()
      markGeometry.dispose()
      markMaterial.dispose()
      reticleGeometry.dispose()
      reticleMaterial.dispose()
      labelMaterial.dispose()
      texture?.dispose()
    },
  }
}
